import { nextWorkoutDate, type RepwiseEvent } from '@repwise/domain';
import { uuidv7 } from '@repwise/sync';
import { useEffect, useState } from 'react';
import { useRepo } from '../lib/hooks.ts';
import { cancelReminder, morningOf, pushState, scheduleReminder } from '../lib/push.ts';

const REMIND_KEY = 'repwise.remindNext'; // per device: push subscriptions are per device
const readRemind = () => {
  try {
    return localStorage.getItem(REMIND_KEY) === '1';
  } catch {
    return false;
  }
};

const label = (iso: string, today: string) => {
  if (iso === today) return 'today';
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
};

/**
 * J19: "Next workout: Wed 15 Oct ▾". The default comes from the plan's days a week and the last workout; choosing
 * a day writes next_date_set. It moves nothing in the programme.
 */
export function NextDate({ events, daysPerWeek, workout }: { events: RepwiseEvent[]; daysPerWeek: number; workout?: string }) {
  const repo = useRepo();
  const { store } = repo;
  const [remind, setRemind] = useState(readRemind);
  const [open, setOpen] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = Date.now();
  const { date, planned } = nextWorkoutDate(events, daysPerWeek, now, tz);
  const today = nextWorkoutDate([], 1, now, tz).date;
  const at = morningOf(date);
  // Keep the one reminder in step with the next workout's day (re-sent whenever the day changes).
  useEffect(() => {
    if (remind && at > Date.now()) scheduleReminder(repo, at, workout ? `Workout ${workout} is ready in Repwise.` : 'Your next workout is ready in Repwise.');
  }, [remind, at, workout, repo]);
  const toggle = (on: boolean) => {
    setRemind(on);
    try {
      on ? localStorage.setItem(REMIND_KEY, '1') : localStorage.removeItem(REMIND_KEY);
    } catch {
      /* storage unavailable: this visit only */
    }
    if (!on) cancelReminder(repo);
  };
  const canPush = pushState() === 'on';
  return (
    <div className="text-sm">
      <button type="button" className="min-h-11 inline-flex items-center gap-1 text-text-muted" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Next workout: <span className="text-text">{label(date, today)}</span>
        {planned ? ' (planned)' : ''} ▾
      </button>
      {open && (
        <label className="block mt-1">
          Plan a day
          <input
            type="date"
            className="block mt-1 min-h-11 rounded-[10px] bg-surface-sunken border border-outline px-2"
            min={today}
            value={date}
            onChange={(e) => {
              const v = e.target.value;
              if (/^\d{4}-\d{2}-\d{2}$/.test(v)) void store.append([{ type: 'next_date_set', id: uuidv7(), at: Date.now(), date: v }]).then(() => setOpen(false));
            }}
          />
          <span className="block text-xs text-text-muted mt-1">This doesn't change your programme.</span>
        </label>
      )}
      {open && (
        <label className="flex items-center gap-2 mt-2 min-h-11">
          <input type="checkbox" checked={remind && canPush} disabled={!canPush} onChange={(e) => toggle(e.target.checked)} />
          Remind me at 8:00 that morning
          {!canPush && <span className="text-xs text-text-muted">(turn on alerts in Settings first)</span>}
          {canPush && remind && at <= Date.now() && <span className="text-xs text-text-muted">(already past 8:00 today)</span>}
        </label>
      )}
    </div>
  );
}
